import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";

type Subscriber = {
  id: string;
  email: string;
  firstName: string | null;
  source: string | null;
  status: "active" | "unsubscribed";
  createdAt: Date | string;
};

type SubscriberSortKey = "email" | "name" | "source" | "status" | "date";

function subscriberSearchText(subscriber: Subscriber) {
  return [
    subscriber.email,
    subscriber.firstName,
    subscriber.source,
    subscriber.status,
    formatDate(subscriber.createdAt),
  ]
    .filter(Boolean)
    .join(" ");
}

const subscriberSorters: Record<SubscriberSortKey, (row: Subscriber) => string | number> = {
  email: (row) => row.email,
  name: (row) => row.firstName ?? "",
  source: (row) => row.source ?? "",
  status: (row) => row.status,
  date: (row) => dateSortValue(row.createdAt),
};

export default function AdminSubscribers() {
  const [list, setList] = useState<Subscriber[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api
      .get<{ subscribers: Subscriber[] }>("/api/admin/subscribers")
      .then((r) => setList(r.subscribers))
      .finally(() => setLoaded(true));
  }, []);

  const filters = useMemo(
    () => [(row: Subscriber) => statusFilter === "all" || row.status === statusFilter],
    [statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Subscriber, SubscriberSortKey>({
    rows: list,
    search,
    initialSort: { key: "date", direction: "desc" },
    sorters: subscriberSorters,
    searchText: subscriberSearchText,
    filters,
  });

  const csvUrl = `${import.meta.env.VITE_API_URL ?? ""}/api/admin/subscribers/export.csv`;

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Subscribers</h1>
        <a href={csvUrl} download className="btn-ghost">
          Download CSV
        </a>
      </div>

      {!loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search subscribers, names, sources..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "unsubscribed", label: "Unsubscribed" },
              ]}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No subscribers yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No subscribers match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Email" sortKey="email" sort={sort} onSort={requestSort} />
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={requestSort} />
                  <SortableTh label="Source" sortKey="source" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <SortableTh label="Date" sortKey="date" sort={sort} onSort={requestSort} />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((s) => (
                  <tr key={s.id}>
                    <td>{s.email}</td>
                    <td>{s.firstName ?? <span className="empty">-</span>}</td>
                    <td>{s.source ?? <span className="empty">-</span>}</td>
                    <td>
                      <span className={`badge badge-${s.status}`}>{s.status}</span>
                    </td>
                    <td>{formatDate(s.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .empty { color: rgba(8,45,61,0.35); }

  .badge { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; }
  .badge-active { background: #e6f4ea; color: #2d7a3a; }
  .badge-unsubscribed { background: ${colors.blushLight}; color: ${colors.berryRose}; }
`;
